export function normalizeIndex(originalIndex: number, length: number): number {
  let index = originalIndex;

  if (index < 0) {
    index = length + index;

    if (index < 0) {
      throw new Error(`Index out of bounds: ${originalIndex}`);
    }
  }

  if (index >= length) {
    throw new Error(`Index out of bounds: ${originalIndex}`);
  }

  return index;
}

declare const DimensionSymbol: unique symbol;

export interface Dimension<Name extends string> {
  readonly [DimensionSymbol]: Name;
}

export type VectorObject = {
  data: number[];
};

export default class Vector<D extends string = "dim"> {
  data: Float64Array;
  [n: number]: never;

  private readonly _dim!: Dimension<D>;

  constructor(data: number[] | Float64Array) {
    this.data = data instanceof Float64Array ? data : new Float64Array(data);
  }

  toObject(): VectorObject {
    return { data: Array.from(this.data) };
  }

  static fromObject<D extends string>(object: VectorObject): Vector<D> {
    return new Vector(object.data);
  }

  get length(): number {
    return this.data.length;
  }

  static zeros<D extends string>(length: number): Vector<D> {
    return new Vector(Array(length).fill(0));
  }

  static ones<D extends string>(length: number): Vector<D> {
    return new Vector(Array(length).fill(1));
  }

  get(index: number): number {
    const normalizedIndex = normalizeIndex(index, this.length);
    const value = this.data[normalizedIndex];

    if (value == null) {
      throw new Error(`Index out of bounds: ${index}`);
    }

    return value;
  }

  set(index: number, value: number): void {
    const normalizedIndex = normalizeIndex(index, this.length);
    this.data[normalizedIndex] = value;
  }

  zerosLike(): Vector<D> {
    return new Vector(Array(this.length).fill(0));
  }

  abs(): Vector<D> {
    return new Vector(this.data.map((value) => Math.abs(value)));
  }

  sum(): number {
    return this.data.reduce((acc, curr) => acc + curr, 0);
  }

  add(vector: Vector<D>): Vector<D> {
    return new Vector(
      this.data.map((value, index) => value + vector.get(index)),
    );
  }

  subtract(vector: Vector<D>): Vector<D> {
    return new Vector(
      this.data.map((value, index) => value - vector.get(index)),
    );
  }

  multiply(scalar: number): Vector<D> {
    return new Vector(this.data.map((value) => value * scalar));
  }

  /** Return the sorted unique elements of the vector. */
  unique(): Vector<D> {
    return new Vector(Array.from(new Set(this.data)).sort((a, b) => a - b));
  }

  [Symbol.iterator]() {
    return this.data[Symbol.iterator]();
  }

  map(fn: (value: number, index: number) => number): Vector<D> {
    return new Vector(this.data.map(fn));
  }

  filter<C extends string>(
    fn: (value: number, index: number) => boolean,
  ): Vector<C> {
    return new Vector(this.data.filter(fn));
  }

  normalize(): Vector<D> {
    const sum = this.sum();
    return this.map((value) => value / sum);
  }

  includes(value: number): boolean {
    return this.data.includes(value);
  }

  forEach(fn: (value: number, index: number) => void): void {
    this.data.forEach(fn);
  }

  some(fn: (value: number, index: number) => boolean): boolean {
    return this.data.some(fn);
  }

  shift<C extends string>(): [number, Vector<C>] {
    if (this.length === 0) {
      throw new Error("Cannot shift an empty vector");
    }

    const first = this.data[0];

    if (first == null) {
      throw new Error("Cannot shift an empty vector");
    }

    return [first, new Vector(this.data.slice(1))];
  }

  toArray(): number[] {
    return Array.from(this.data);
  }

  rename<NewD extends string>(_dimRename: `${D} -> ${NewD}`): Vector<NewD> {
    return this as unknown as Vector<NewD>;
  }

  max(): number {
    return Math.max(...this.data);
  }
}
